import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import useApi from 'hooks/useApi';

interface LocationTrackingSettingsProps {
  teamUid: string;
  clientUID?: string;
}

const roleId = (rt: any): string => (rt?.id || rt?._id)?.toString();

/**
 * Process-level "Location Tracking" — an on/off switch on the flow, scoped to
 * one or more roles (ResourceTypes with category 'user').
 *
 * When enabled, the mobile app requires the user to start live location
 * tracking when they enter a transaction, for the whole transaction, until it
 * reaches a terminal state (All Done / Denied / Rest / Swapped / Stop). The
 * tracking dialog only appears for users whose role is in the selected set —
 * by default all roles. The backend resolves the role check, so the app simply
 * receives the final on/off decision for the current user.
 */
const LocationTrackingSettings: React.FC<LocationTrackingSettingsProps> = ({ teamUid, clientUID }) => {
  const { getLocationTracking, updateLocationTracking, getResourceTypes, showToast } = useApi();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [resourceTypes, setResourceTypes] = useState<any[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const [cfgRes, rtRes] = await Promise.allSettled([
          getLocationTracking(teamUid),
          getResourceTypes(clientUID, 'user'),
        ]);
        if (!active) return;

        const roles = rtRes.status === 'fulfilled' ? rtRes.value?.data : null;
        const roleList = Array.isArray(roles) ? roles : [];
        setResourceTypes(roleList);

        const cfg = cfgRes.status === 'fulfilled' ? (cfgRes.value?.data ?? cfgRes.value) : null;
        setEnabled(cfg?.locationTracking ?? false);
        const saved: string[] = Array.isArray(cfg?.locationTrackingRoles) ? cfg.locationTrackingRoles : [];
        // Empty saved set = "all roles" — pre-select every role so the admin
        // sees the default and can narrow it.
        setSelectedRoles(saved.length ? saved : roleList.map(roleId).filter(Boolean));
      } catch (e) {
        // keep defaults (off) on failure
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [teamUid, clientUID]);

  const handleToggle = (checked: boolean) => {
    setEnabled(checked);
    // Turning on with nothing selected → default to all roles.
    if (checked && selectedRoles.length === 0) {
      setSelectedRoles(resourceTypes.map(roleId).filter(Boolean));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateLocationTracking(teamUid, {
        locationTracking: enabled,
        locationTrackingRoles: selectedRoles,
      });
      showToast('Location tracking settings saved', 'success');
    } catch (e) {
      showToast('Failed to save location tracking settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, m: 2 }}>
      <CardContent>
        <Typography variant="h5" fontWeight={700} mb={0.5}>
          Location Tracking
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Track the live location of mobile app users for the duration of each transaction in this
          process. When enabled, users must start tracking when they enter a transaction; tracking
          stops automatically when the transaction reaches an end state (All Done, Denied, Rest,
          Swapped, or Stop). When disabled, no location permission or tracking is requested.
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
            px: 2,
            borderRadius: 2,
            bgcolor: enabled ? 'rgba(30,142,78,0.06)' : 'grey.50',
            border: '1px solid',
            borderColor: enabled ? 'rgba(30,142,78,0.25)' : 'divider',
          }}
        >
          <Box sx={{ pr: 2 }}>
            <Typography fontWeight={600}>Enable live location tracking</Typography>
            <Typography variant="caption" color="text.secondary">
              Mobile app users in this process are tracked from transaction entry to completion.
            </Typography>
          </Box>
          <Switch checked={enabled} onChange={(e) => handleToggle(e.target.checked)} color="success" />
        </Box>

        <Collapse in={enabled} unmountOnExit>
          <Box
            sx={{
              mt: 1.5,
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
            }}
          >
            <Typography fontWeight={600} mb={0.5}>
              Apply to roles
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Only users with a selected role are asked to start tracking when they open a
              transaction. Leave all selected to track everyone.
            </Typography>
            <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                value={selectedRoles}
                label="Roles"
                onChange={(e) => setSelectedRoles(e.target.value as string[])}
                renderValue={(sel) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(sel as string[]).map((v) => {
                      const rt = resourceTypes.find((r: any) => roleId(r) === v);
                      return <Chip key={v} label={rt?.name || v} size="small" />;
                    })}
                  </Box>
                )}
              >
                {resourceTypes.map((rt: any) => (
                  <MenuItem key={roleId(rt)} value={roleId(rt)}>
                    {rt.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {resourceTypes.length === 0 && (
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                No user roles found for this client — tracking will apply to all users.
              </Typography>
            )}
          </Box>
        </Collapse>

        <Alert severity="info" sx={{ mt: 2 }}>
          The separate “Location” workflow step is no longer needed — tracking is handled
          automatically for the whole transaction when this setting is on.
        </Alert>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" color="secondary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default LocationTrackingSettings;
